import { formatMoney } from './money-formatting.js';
import { onAnimationEnd } from './utilities.js';

/**
 * Bundle Sticky Footer Component
 * Displays item count, pricing, quantity picker, and Add to Cart.
 * Chevron triggers bundle summary popup modal.
 */
class BundleStickyFooter extends HTMLElement {
  /** @type {Map<string, {productTitle: string, retailPrice: number, bundlePrice: number, compareAtPrice: number|null}>} */
  #items = new Map();

  #quantity = 1;
  #maxQuantity = 99;
  #limitingProduct = null;

  /** @type {AbortController} */
  #controller;

  /** @type {string} Collection handle that triggers bundle discounts */
  #discountTriggerCategory = 'bundle-builder-card-readers-category';

  /** @type {Object} Translated UI strings */
  #text = {};

  // --- Cached DOM references ---
  #priceDisplay;
  #comparePrice;
  #subtitleText;
  #addToCartButton;
  #buttonText;
  #summaryTrigger;
  #quantityValue;
  #decreaseBtn;
  #increaseBtn;
  #summaryModal;
  #summaryItemsContainer;
  #discountBanner;
  #bannerText;
  #savingsAmount;
  #savingsDescription;
  #totalAmount;
  #closeModalBtn;
  #modalQuantityValue;
  #modalDecreaseBtn;
  #modalIncreaseBtn;
  #modalAddToCartBtn;
  #modalButtonText;
  #footerError;
  #modalError;
  #liveRegion;

  connectedCallback() {
    this.#controller = new AbortController();

    this.#discountTriggerCategory = this.dataset.discountTriggerCategory || 'bundle-builder-card-readers-category';

    this.#text = {
      addToCart: this.dataset.textAddToCart || 'Add to Cart',
      adding: this.dataset.textAdding || 'Adding...',
      added: this.dataset.textAdded || 'Added to cart',
      error: this.dataset.textError || 'Error - try again',
      item: this.dataset.textItem || 'item',
      items: this.dataset.textItems || 'items',
      bundle: this.dataset.textBundle || 'bundle',
      discountApplied: this.dataset.textDiscountApplied || 'Discount applied',
      addItemToSave: this.dataset.textAddItemToSave || 'Add 1 item to save',
      addCardReaderToSave: this.dataset.textAddCardReaderToSave || 'Add a card reader to save',
      addBundleItemsToSave: this.dataset.textAddBundleItemsToSave || 'Add 1 card reader + 1 or more items to save',
      savingsDescription: this.dataset.textSavingsDescription || '1 card reader + 1 or more items bundled',
      saleApplied: this.dataset.textSaleApplied || 'Sale applied',
      empty: this.dataset.textEmpty || 'Select items to build your bundle',
      stockLimit: this.dataset.textStockLimit || 'Only %{max} available (limited by %{product})',
    };

    this.#cacheElements();
    this.#bindEvents();
    this.#render();
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  #cacheElements() {
    // Footer elements
    this.#priceDisplay = this.querySelector('[data-price-display]');
    this.#comparePrice = this.querySelector('[data-compare-price]');
    this.#subtitleText = this.querySelector('[data-subtitle-text]');
    this.#addToCartButton = this.querySelector('[data-action="add-to-cart"]');
    this.#buttonText = this.querySelector('[data-button-text]');
    this.#summaryTrigger = this.querySelector('[data-action="open-summary"]');

    // Footer quantity picker
    this.#quantityValue = this.querySelector('[data-quantity-value]');
    this.#decreaseBtn = this.querySelector('[data-action="decrease"]');
    this.#increaseBtn = this.querySelector('[data-action="increase"]');

    // Modal elements
    this.#summaryModal = this.querySelector('[data-summary-modal]');
    this.#summaryItemsContainer = this.querySelector('[data-summary-items]');
    this.#discountBanner = this.querySelector('[data-discount-banner]');
    this.#bannerText = this.querySelector('[data-banner-text]');
    this.#savingsAmount = this.querySelector('[data-savings-amount]');
    this.#savingsDescription = this.querySelector('[data-savings-description]');
    this.#totalAmount = this.querySelector('[data-total-amount]');
    this.#closeModalBtn = this.querySelector('[data-action="close-summary"]');

    // Modal quantity picker
    this.#modalQuantityValue = this.querySelector('[data-modal-quantity-value]');
    this.#modalDecreaseBtn = this.querySelector('[data-action="modal-decrease"]');
    this.#modalIncreaseBtn = this.querySelector('[data-action="modal-increase"]');
    this.#modalAddToCartBtn = this.querySelector('[data-action="modal-add-to-cart"]');
    this.#modalButtonText = this.querySelector('[data-modal-button-text]');

    // Error elements
    this.#footerError = this.querySelector('[data-bundle-error="footer"]');
    this.#modalError = this.querySelector('[data-bundle-error="modal"]');
    this.#liveRegion = this.querySelector('[data-bundle-live-region]');
  }

  #bindEvents() {
    const opts = { signal: this.#controller.signal };

    // Single source of truth: only listen for authoritative state snapshots
    // from the configurator (bundle:state-changed). Individual product-selected /
    // product-deselected events are consumed by other components (e.g. gallery)
    // but NOT by the footer — this prevents stale entries accumulating.
    document.addEventListener('bundle:state-changed', (event) => {
      this.#handleStateChanged(event);
    }, opts);

    this.#summaryTrigger?.addEventListener('click', () => this.#openSummaryModal(), opts);
    this.#closeModalBtn?.addEventListener('click', () => this.#closeSummaryModal(), opts);
    this.#summaryModal?.addEventListener('click', (event) => {
      if (event.target === this.#summaryModal) this.#closeSummaryModal();
    }, opts);

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.#summaryModal?.open) {
        this.#closeSummaryModal();
      }
    }, opts);

    this.#decreaseBtn?.addEventListener('click', () => this.#updateQuantity(-1), opts);
    this.#increaseBtn?.addEventListener('click', () => this.#updateQuantity(1), opts);
    this.#modalDecreaseBtn?.addEventListener('click', () => this.#updateQuantity(-1), opts);
    this.#modalIncreaseBtn?.addEventListener('click', () => this.#updateQuantity(1), opts);

    this.#addToCartButton?.addEventListener('click', (e) => {
      e.preventDefault();
      this.#handleAddToCart('footer');
    }, opts);

    this.#modalAddToCartBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      this.#handleAddToCart('modal');
    }, opts);

    document.addEventListener('bundle:cart-add-success', () => {
      this.#setButtonState('success');
      this.#closeSummaryModal();
    }, opts);

    document.addEventListener('bundle:cart-add-error', (event) => {
      this.#setButtonState('error');
      this.#showError(event.detail?.error || this.#text.error);
    }, opts);

    document.addEventListener('bundle:max-quantity-changed', (event) => {
      this.#handleMaxQuantityChanged(event);
    }, opts);
  }

  // ---------------------------------------------------------------------------
  // Modal
  // ---------------------------------------------------------------------------

  #openSummaryModal() {
    if (!this.#summaryModal || this.#items.size === 0) return;
    this.#renderModalItems();
    this.#renderModalSummary();
    this.#syncQuantityToModal();
    this.#summaryModal.showModal();
    document.body.style.overflow = 'hidden';
  }

  #closeSummaryModal() {
    if (!this.#summaryModal) return;
    this.#summaryModal.close();
    document.body.style.overflow = '';
  }

  #renderModalItems() {
    if (!this.#summaryItemsContainer) return;

    const itemsHtml = [];
    for (const [category, item] of this.#items) {
      itemsHtml.push(`
        <div class="bundle-summary-modal__item" data-category="${category}">
          <span class="bundle-summary-modal__item-name">${this.#escapeHtml(item.productTitle)}</span>
          <span class="bundle-summary-modal__item-price">${this.#formatPrice(item.retailPrice)}</span>
        </div>
      `);
    }
    this.#summaryItemsContainer.innerHTML = itemsHtml.join('');
  }

  #renderModalSummary() {
    const { totalRetail, totalBundle, discountApplies } = this.#computeTotals();
    const savings = discountApplies ? totalRetail - totalBundle : 0;
    const displayTotal = discountApplies ? totalBundle : totalRetail;

    if (this.#savingsAmount) {
      const savingsRow = this.#savingsAmount.closest('.bundle-summary-modal__savings');
      if (savings > 0) {
        this.#savingsAmount.textContent = `-${this.#formatPrice(savings)}`;
      }
      savingsRow?.classList.toggle('bundle-summary-modal__savings--hidden', savings <= 0);
    }

    if (this.#savingsDescription) {
      this.#savingsDescription.textContent = savings > 0 ? this.#text.savingsDescription : '';
    }

    if (this.#totalAmount) {
      this.#totalAmount.textContent = this.#formatPrice(displayTotal);
    }

    this.#renderDiscountBanner(discountApplies, savings);
  }

  #renderDiscountBanner(discountApplies, savings) {
    if (!this.#discountBanner || !this.#bannerText) return;

    const saleApplies = !discountApplies && this.#hasCardReader() && this.#items.size >= 2 && this.#hasOnSaleItems();

    const hideBanner = (discountApplies && savings > 0) || saleApplies;
    this.#discountBanner.classList.toggle('bundle-summary-modal__discount-banner--hidden', hideBanner);

    if (!hideBanner) {
      this.#discountBanner.dataset.bannerState = 'warning';
      this.#bannerText.textContent = this.#text.addBundleItemsToSave;
    }
  }

  #syncQuantityToModal() {
    if (this.#modalQuantityValue) {
      this.#modalQuantityValue.textContent = this.#quantity;
    }
    if (this.#modalDecreaseBtn) {
      this.#modalDecreaseBtn.disabled = this.#quantity <= 1;
    }
    if (this.#modalIncreaseBtn) {
      this.#modalIncreaseBtn.disabled = this.#quantity >= this.#maxQuantity;
    }
  }

  // ---------------------------------------------------------------------------
  // State handling
  // ---------------------------------------------------------------------------

  /**
   * Rebuild items map from the configurator's full state snapshot.
   * This is the authoritative pricing source — overrides any stale
   * entries left by missed individual select/deselect events.
   */
  #handleStateChanged(event) {
    const { selections } = event.detail;
    if (!selections) return;

    this.#items.clear();
    for (const [category, item] of selections) {
      this.#items.set(category, {
        productTitle: item.productTitle,
        retailPrice: item.retailPrice,
        bundlePrice: item.bundlePrice,
        compareAtPrice: item.compareAtPrice,
      });
    }
    this.#render();
  }

  #handleAddToCart(source) {
    if (this.#items.size === 0) return;
    this.#hideError();
    this.#setButtonState('loading');
    document.dispatchEvent(
      new CustomEvent('bundle:add-to-cart-requested', {
        bubbles: true,
        detail: { source, quantity: this.#quantity },
      })
    );
  }

  #updateQuantity(delta) {
    const newQuantity = this.#quantity + delta;
    if (newQuantity >= 1 && newQuantity <= this.#maxQuantity) {
      this.#quantity = newQuantity;
      this.#renderQuantity();
      this.#syncQuantityToModal();
      this.#updateStockLimitTooltip();
    }
  }

  #handleMaxQuantityChanged(event) {
    const { maxQuantity, limitingProduct } = event.detail;
    this.#maxQuantity = maxQuantity;
    this.#limitingProduct = limitingProduct;

    if (this.#quantity > maxQuantity) {
      this.#quantity = maxQuantity;
    }

    this.#renderQuantity();
    this.#syncQuantityToModal();
    this.#updateStockLimitTooltip();
  }

  // ---------------------------------------------------------------------------
  // Pricing helpers
  // ---------------------------------------------------------------------------

  /**
   * Compute totals and discount eligibility from the current items map.
   * Shared by footer and modal rendering to avoid duplicate iteration.
   */
  #computeTotals() {
    let totalRetail = 0;
    let totalBundle = 0;
    let totalCompareAt = 0;

    for (const item of this.#items.values()) {
      totalRetail += item.retailPrice;
      totalBundle += item.bundlePrice;
      totalCompareAt += item.compareAtPrice || item.retailPrice;
    }

    const hasCardReader = this.#hasCardReader();
    const hasNonCardReaderItems = this.#hasNonCardReaderItems();
    const hasOnSaleItems = this.#hasOnSaleItems();
    const discountApplies = hasCardReader && this.#items.size >= 2 && hasNonCardReaderItems;
    const saleApplies = !discountApplies && hasCardReader && this.#items.size >= 2 && hasOnSaleItems;

    return { totalRetail, totalBundle, totalCompareAt, discountApplies, saleApplies };
  }

  #hasCardReader() {
    return this.#items.has(this.#discountTriggerCategory);
  }

  #hasNonCardReaderItems() {
    for (const [category, item] of this.#items) {
      if (category !== this.#discountTriggerCategory && item.bundlePrice < item.retailPrice) {
        return true;
      }
    }
    return false;
  }

  #hasOnSaleItems() {
    for (const [category, item] of this.#items) {
      if (
        category !== this.#discountTriggerCategory &&
        item.compareAtPrice &&
        item.compareAtPrice > item.retailPrice &&
        item.bundlePrice >= item.retailPrice
      ) {
        return true;
      }
    }
    return false;
  }

  /**
   * Format price in cents using shared formatMoney utility.
   * @param {number} cents
   * @returns {string}
   */
  #formatPrice(cents) {
    const moneyFormat = typeof Theme !== 'undefined' ? Theme.moneyFormat : undefined;
    const currency = (typeof Theme !== 'undefined' && Theme.currency?.active) || 'USD';
    return formatMoney(cents, moneyFormat, currency);
  }

  #escapeDiv = document.createElement('div');
  #escapeHtml(str) {
    if (!str) return '';
    this.#escapeDiv.textContent = str;
    return this.#escapeDiv.innerHTML;
  }

  // ---------------------------------------------------------------------------
  // Rendering
  // ---------------------------------------------------------------------------

  #render() {
    const hasItems = this.#items.size > 0;
    this.dataset.hasItems = hasItems.toString();

    if (this.#addToCartButton) {
      this.#addToCartButton.disabled = !hasItems;
    }

    this.#renderQuantity();
    this.#renderFooterState();
  }

  #renderQuantity() {
    if (this.#quantityValue) {
      this.#quantityValue.textContent = this.#quantity;
    }
    if (this.#decreaseBtn) {
      this.#decreaseBtn.disabled = this.#quantity <= 1;
    }
    if (this.#increaseBtn) {
      this.#increaseBtn.disabled = this.#quantity >= this.#maxQuantity;
    }
  }

  #renderFooterState() {
    const { totalRetail, totalBundle, totalCompareAt, discountApplies, saleApplies } = this.#computeTotals();
    const displayPrice = discountApplies ? totalBundle : totalRetail;

    if (this.#priceDisplay) {
      this.#priceDisplay.textContent = this.#formatPrice(displayPrice);
    }

    if (this.#comparePrice) {
      const showCompare = totalCompareAt > displayPrice;
      if (showCompare) {
        this.#comparePrice.textContent = this.#formatPrice(totalCompareAt);
      }
      this.#comparePrice.classList.toggle('lq-bundle-sticky-footer__compare-price--hidden', !showCompare);
    }

    this.dataset.discountEligible = (discountApplies || saleApplies).toString();

    if (this.#subtitleText) {
      const itemCount = this.#items.size;

      if (itemCount === 0) {
        this.#subtitleText.textContent = this.#text.empty;
      } else {
        let message = '';

        if (discountApplies) {
          message = this.#text.discountApplied;
        } else if (this.#items.size < 2) {
          message = this.#text.addItemToSave;
        } else if (!this.#hasCardReader()) {
          message = this.#text.addCardReaderToSave;
        } else if (saleApplies) {
          message = this.#text.saleApplied;
        }

        this.#subtitleText.innerHTML = `${itemCount}-${this.#text.item} ${this.#text.bundle}<span class="lq-bundle-sticky-footer__dot">•</span>${message}`;
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Button state & errors
  // ---------------------------------------------------------------------------

  /**
   * Set button state across footer and modal buttons.
   * Transient states (success/error) set a data-state attribute that triggers
   * a CSS animation; onAnimationEnd resets to default when it completes.
   */
  #setButtonState(state) {
    const buttons = [
      { btn: this.#addToCartButton, text: this.#buttonText },
      { btn: this.#modalAddToCartBtn, text: this.#modalButtonText },
    ];

    // Clear any in-flight transient state animations before applying new state
    for (const { btn } of buttons) {
      if (btn) delete btn.dataset.state;
    }

    switch (state) {
      case 'loading':
        for (const { btn, text } of buttons) {
          if (btn) btn.disabled = true;
          if (text) text.textContent = this.#text.adding;
        }
        break;
      case 'success':
        for (const { btn, text } of buttons) {
          if (text) text.textContent = this.#text.added;
          if (btn) btn.dataset.state = 'success';
        }
        this.#resetButtonAfterAnimation();
        break;
      case 'error':
        for (const { btn, text } of buttons) {
          if (btn) btn.disabled = false;
          if (text) text.textContent = this.#text.error;
          if (btn) btn.dataset.state = 'error';
        }
        this.#resetButtonAfterAnimation();
        break;
      case 'default':
      default:
        for (const { btn, text } of buttons) {
          if (btn) btn.disabled = this.#items.size === 0;
          if (text) text.textContent = this.#text.addToCart;
        }
        break;
    }
  }

  /**
   * Wait for the CSS hold animation on the footer button to finish,
   * then reset both buttons to default state.
   */
  #resetButtonAfterAnimation() {
    if (!this.#addToCartButton) return;
    onAnimationEnd(this.#addToCartButton, () => this.#setButtonState('default'), { subtree: false });
  }

  #showError(message) {
    // Clear any previous error first
    this.#hideError();

    for (const el of [this.#footerError, this.#modalError]) {
      if (!el) continue;
      el.classList.remove('hidden');
      const textEl = el.querySelector('[data-bundle-error-text]');
      if (textEl) textEl.textContent = message;
    }

    if (this.#liveRegion) {
      this.#liveRegion.textContent = message;
    }
  }

  #hideError() {
    for (const el of [this.#footerError, this.#modalError]) {
      if (!el) continue;
      el.classList.add('hidden');
      const textEl = el.querySelector('[data-bundle-error-text]');
      if (textEl) textEl.textContent = '';
    }

    if (this.#liveRegion) {
      this.#liveRegion.textContent = '';
    }
  }

  #updateStockLimitTooltip() {
    const atCap = this.#maxQuantity < 99 && this.#quantity >= this.#maxQuantity;
    const tooltip =
      atCap && this.#limitingProduct
        ? this.#text.stockLimit.replace('%{max}', this.#maxQuantity).replace('%{product}', this.#limitingProduct)
        : null;

    for (const btn of [this.#increaseBtn, this.#modalIncreaseBtn]) {
      if (!btn) continue;
      if (tooltip) {
        btn.dataset.tooltip = tooltip;
      } else {
        delete btn.dataset.tooltip;
      }
    }
  }

}

if (!customElements.get('bundle-sticky-footer')) {
  customElements.define('bundle-sticky-footer', BundleStickyFooter);
}
